/**
 * Billing API client + Razorpay checkout helper.
 */
import axios from 'axios';
import { usePortalStore, useAdminStore } from '../stores';

const API = process.env.REACT_APP_BACKEND_URL;

const authClient = axios.create({ baseURL: `${API}/api` });
authClient.interceptors.request.use((cfg) => {
  const token = usePortalStore.getState().token;
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Separate client for admin endpoints — uses the admin store JWT.
const adminClient = axios.create({ baseURL: `${API}/api` });
adminClient.interceptors.request.use((cfg) => {
  const token = useAdminStore.getState().token;
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const publicClient = axios.create({ baseURL: `${API}/api` });

export const billingApi = {
  // Public
  publicPricing: (country) => publicClient.get('/billing/public/pricing', { params: { country } }),
  publicQuote: (credits, currency, model) => publicClient.post('/billing/public/quote', { credits, currency, model }),

  // Portal
  pricing: () => authClient.get('/portal/billing/pricing'),
  wallet: () => authClient.get('/portal/billing/wallet'),
  quote: (credits, currency) => authClient.post('/portal/billing/quote', { credits, currency }),
  createOrder: (body) => authClient.post('/portal/billing/orders/create', body),
  verifyOrder: (body) => authClient.post('/portal/billing/orders/verify', body),
  transactions: (params) => authClient.get('/portal/billing/transactions', { params }),
  invoices: () => authClient.get('/portal/billing/invoices'),
  invoicePdf: (id) => authClient.get(`/portal/billing/invoices/${id}/pdf`, { responseType: 'blob' }),
  setAutoTopup: (body) => authClient.put('/portal/billing/auto-topup', body),
  getAutoTopup: () => authClient.get('/portal/billing/auto-topup'),
  usageSummary: (days = 30) => authClient.get('/portal/billing/usage/summary', { params: { days } }),
  // Records the actual Razorpay error code from a failed checkout so
  // support can grep recurring failure patterns server-side instead of
  // chasing screenshots of the opaque "Something went wrong" toast.
  logCheckoutError: (body) => authClient.post('/portal/billing/checkout-errors', body),
};

export const adminBillingApi = {
  summary: () => adminClient.get('/admin/billing/summary'),
  users: (params) => adminClient.get('/admin/billing/users', { params }),
  transactions: (params) => adminClient.get('/admin/billing/transactions', { params }),
  invoices: (params) => adminClient.get('/admin/billing/invoices', { params }),
  webhookHealth: () => adminClient.get('/admin/billing/webhook-health'),
  runWebhookProbe: () => adminClient.post('/admin/billing/webhook-health/run'),
};

// Razorpay checkout helper (demo mode returns a simulated success)
export async function openRazorpayCheckout({ order, razorpay_key_id, demo_mode, user, credits, price, onSuccess, onDismiss, onError }) {
  if (demo_mode) {
    // Demo: auto-succeed after a short delay, using the well-known demo signature
    if (onDismiss) window.__dismissDemo = onDismiss;
    return onSuccess({
      razorpay_order_id: order.id,
      razorpay_payment_id: `pay_demo_${Date.now()}`,
      razorpay_signature: 'demo_sig',
    });
  }
  if (!window.Razorpay) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://checkout.razorpay.com/v1/checkout.js';
      s.onload = resolve; s.onerror = reject;
      document.body.appendChild(s);
    });
  }
  return new Promise((resolve) => {
    // IMPORTANT: when `order_id` is provided, Razorpay derives amount and
    // currency from the order itself. Passing them again CAN cause the
    // checkout to throw a generic "Something went wrong" if they don't
    // match exactly (e.g. integer-rounding skew in cents). We pass them
    // anyway because checkout.js v1 requires `amount` even with order_id,
    // but they MUST match the server-side order.
    const opts = {
      key: razorpay_key_id,
      order_id: order.id,
      amount: order.amount,
      currency: order.currency,
      name: 'Cognara',
      description: `${credits} credits`,
      // International USD payments REQUIRE a contact number on the order
      // — without it Razorpay rejects the checkout with the generic
      // "Something went wrong" error before the user can pick a method.
      prefill: {
        name: user?.name || 'Customer',
        email: user?.email || '',
        contact: user?.phone || user?.contact || '',
      },
      notes: { credits: String(credits), order_currency: order.currency },
      theme: { color: '#F26B1F' },
      handler: (resp) => {
        resolve(resp);
        onSuccess(resp);
      },
      modal: {
        ondismiss: () => { if (onDismiss) onDismiss(); resolve(null); },
        escape: true,
        backdropclose: false,
      },
    };
    const rz = new window.Razorpay(opts);
    // Surface Razorpay's actual error code/description instead of the
    // opaque "Something went wrong" toast. Without this listener,
    // checkout.js swallows the underlying reason and the user sees a
    // generic message. With it, we get the structured `error.code`,
    // `error.description`, `error.source`, `error.step`, `error.reason`
    // — exactly what's needed to debug live-mode failures.
    rz.on('payment.failed', (resp) => {
      const err = resp?.error || {};
      // eslint-disable-next-line no-console
      console.error('[Razorpay] payment.failed', err);
      if (onError) onError(err);
      resolve(null);
    });
    rz.open();
  });
}
